// connectionFailureHoldByDevice mutations + accessors — pure over an immer draft (no zustand/React/proxy).
// Holds the FAILED verdict steady for the terminal-verdict surfaces (needs-attention chip, "Configure Video" toast)
// across the encoder's ~5s CONNECTION_FAILED<->CONNECTING retry cycle; the badge keeps its cycle. Adopt on a due
// verdict; release on a real CONNECTED, or when the device/bridge goes away (a hold must never outlive presence).
import type { Device } from '@studio/models/device';
import type { MultiAddSkipReason } from '#~/notifications/types.js';
import type { Draft, State } from '#~/depot/types.js';

/**
 * Stable empty reference. Consumers feed the hold into a `useMemo` dep array,
 * so a fresh `[]` per render would invalidate it every tick.
 */
export const NO_CONNECTION_HOLD: readonly MultiAddSkipReason[] = [];

// Control (loose) release predicate: CONNECTED(3) or UNKNOWN(0)/undefined release; CONNECTING(1)/FAILED(2) hold.
// UNKNOWN/absent releases because a driver retarget removes the control field (else "Connect controls" sticks). Video uses the strict isVideoAspectResolved.
export const isAspectResolved = (status: number | undefined): boolean => {
  if (status === undefined) return true;
  // 0 UNKNOWN, 1 CONNECTING, 2 FAILED, 3 CONNECTED — pinned in
  // chip-state.overall-gate.test.ts.
  return status === 0 || status === 3;
};

// Video (strict) release: ONLY a genuine CONNECTED(3). A momentary UNKNOWN(0)/absent on a CONNECTING leg is jitter,
// not "aspect gone" — treating it as resolved blinked the "Connect video" chip/toast off on every retry leg.
export const isVideoAspectResolved = (status: number | undefined): boolean => status === 3;

/** Buckets this hold governs — the two CONNECTION verdicts, nothing else. */
const HELD_BUCKETS: readonly MultiAddSkipReason[] = ['video-failed', 'control-failed'];

/** Whether a bucket is one this hold governs. */
export const isHeldBucket = (bucket: MultiAddSkipReason): boolean => HELD_BUCKETS.includes(bucket);

/**
 * What the caller observed about a device on this tick.
 *
 * `missing` is the freshly-derived bucket list (already device-level gated).
 * The two `*Connected` flags are the RELEASE signal, and `present` is false
 * when the bridge is offline or the device is gone.
 */
export interface ConnectionObservation {
  missing: readonly MultiAddSkipReason[];
  // Aspect stopped reporting a failure (release signal). Control also releases on UNKNOWN/absent; video only on CONNECTED.
  videoResolved: boolean;
  controlResolved: boolean;
  present: boolean;
}

/**
 * Fold an observation into the hold for one device.
 *
 * Returns the same draft reference untouched when nothing changes, so a
 * caller inside a render-driven effect does not churn subscribers on every
 * poll tick (the same no-op discipline `transitory.ts` follows).
 */
export const noteConnectionObservation = (
  draft: Draft,
  deviceId: Device['id'],
  observation: ConnectionObservation
): void => {
  const held = new Set<string>(draft.connectionFailureHoldByDevice[deviceId] ?? []);
  const before = held.size;

  // Device gone or bridge offline — drop the hold entirely.
  if (!observation.present) {
    if (before > 0) delete draft.connectionFailureHoldByDevice[deviceId];
    return;
  }

  // Adopt every freshly-observed connection bucket.
  observation.missing.filter(isHeldBucket).forEach((bucket) => held.add(bucket));

  // Release per aspect the moment it stops reporting a failure.
  if (observation.videoResolved) held.delete('video-failed');
  if (observation.controlResolved) held.delete('control-failed');

  if (held.size === 0) {
    if (before > 0) delete draft.connectionFailureHoldByDevice[deviceId];
    return;
  }

  // Only write when the set actually changed — key order is irrelevant, so
  // compare by size plus membership.
  const next = HELD_BUCKETS.filter((bucket) => held.has(bucket));
  const current = draft.connectionFailureHoldByDevice[deviceId];
  if (
    current !== undefined &&
    current.length === next.length &&
    next.every((bucket, i) => current[i] === bucket)
  ) {
    return;
  }
  draft.connectionFailureHoldByDevice[deviceId] = next;
};

// Drop a device's hold outright. The per-tile writer unmounts with its tile and a Save/replaceDevice briefly empties
// the room, so the always-mounted notification observer releases from here (orphan sweep / Save) instead.
export const releaseConnectionHold = (draft: Draft, deviceId: string): void => {
  if (deviceId in draft.connectionFailureHoldByDevice) {
    delete draft.connectionFailureHoldByDevice[deviceId];
  }
};

/** Read accessor against a snapshot. Empty array when nothing is held. */
export const getConnectionHold = (
  state: State,
  // Plain `string`, not `Device['id']`: the slice is keyed by string and the
  // notification observer iterates raw origin keys.
  deviceId: string
): readonly MultiAddSkipReason[] =>
  state.connectionFailureHoldByDevice[deviceId] ?? NO_CONNECTION_HOLD;

// Orphaned (sweep-releasable) ONLY when genuinely gone: absent from every room's device list AND not a live multi-add origin.
// Keyed on room presence, not origin membership: a persisted in-room failing camera keeps its per-tile hold (else the chip/toast flap with the 5s oscillation).
export const isConnectionHoldOrphaned = (
  deviceId: string,
  deviceIdsInRoom: ReadonlySet<string>,
  originIds: ReadonlySet<string>
): boolean => !deviceIdsInRoom.has(deviceId) && !originIds.has(deviceId);
